// frontend/src/features/production/serialization/pages/SerialUnitRegisterPage.tsx
//
// ADR-104 — main operator screen. Register a serial unit for the current
// station's routing operation, either by typing or scanning (Rule SN-09).
// Surfaces two specific hard-block errors distinctly so the operator knows
// exactly what to do next:
//   - Rule SN-08: wrong material for this station (left/right mismatch etc.)
//   - AM-020 Rule SN-14: no pallet feed session open on this station

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { useSerialUnitCreate } from "../api/serializationApi";
import { useSerializationStore } from "../store/serializationStore";
import type { SerialUnitSourceMode } from "../api/serializationApi";
import { PageBanner } from "@/shared/components/PageBanner";
import {
  useV1InventoryMaterialRevisionsList,
  useV1ProductionRoutingOperationsList,
  useV1ProductionWorkOrdersList,
} from "@/api/generated/v1/v1";

const SOURCE_MODE_OPTIONS: { value: SerialUnitSourceMode; label: string }[] = [
  { value: "at_lot_intake", label: "هنگام ورود به انبار" },
  { value: "during_production", label: "حین تولید" },
  { value: "at_completion", label: "پایان تولید" },
];

export function SerialUnitRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { workOrderId, routingOperationId, setStation } = useSerializationStore();

  const materialRevisionIdFromUrl = searchParams.get("material_revision_id") ?? "";
  const [materialRevisionId, setMaterialRevisionId] = useState(materialRevisionIdFromUrl);
  const [sourceMode, setSourceMode] = useState<SerialUnitSourceMode>("during_production");
  const [lastCreated, setLastCreated] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: materialRevisionsData } = useV1InventoryMaterialRevisionsList({ page_size: 1000 } as any);
  const { data: routingOperationsData } = useV1ProductionRoutingOperationsList({ page_size: 1000 } as any);
  const { data: workOrdersData } = useV1ProductionWorkOrdersList({ page_size: 1000 } as any);

  const { mutate: createUnit, isPending } = useSerialUnitCreate();

  const handleSubmit = (serialNumber: string) => {
    if (!materialRevisionId) {
      setErrorMessage("ابتدا نسخهٔ متریال را مشخص کنید.");
      return;
    }
    setErrorMessage(null);
    createUnit(
      {
        data: {
          material_revision_id: materialRevisionId,
          source_mode: sourceMode,
          serial_number: serialNumber,
          work_order_id: workOrderId ?? undefined,
          routing_operation_id: routingOperationId ?? undefined,
        },
      } as any,
      {
        onSuccess: (resp: any) => {
          setLastCreated(resp?.serial_number ?? serialNumber);
        },
        onError: (err: any) => {
          // Backend messages (Rule SN-08 / AM-020 Rule SN-14) are already
          // human-readable Persian sentences — surface them directly.
          const detail =
            err?.response?.data?.detail ??
            err?.response?.data?.[0] ??
            "ثبت سریال با خطا مواجه شد.";
          setErrorMessage(Array.isArray(detail) ? detail.join(" ") : String(detail));
        },
      }
    );
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }} dir="rtl">
      <PageBanner
        title="ثبت شمارهٔ سریال قطعه/محصول"
        subtitle="رهگیری دقیق خط تولید — ثبت سریال برای ایستگاه جاری"
        icon="🏷️"
        helpLink="/help/production/serialization/serial-unit-register"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "640px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
            شناسهٔ نسخهٔ متریال (MaterialRevision)
          </label>
          <select
            value={materialRevisionId}
            onChange={(e) => setMaterialRevisionId(e.target.value)}
            dir="ltr"
            style={{ width: "100%", padding: "0.6rem 0.9rem", borderRadius: "8px", background: "#fff", border: "1px solid #ccc" }}
          >
            <option value="">انتخاب کنید...</option>
            {materialRevisionsData?.results?.map((mr: any) => (
              <option key={mr.id} value={mr.id}>
                {mr.name ?? mr.id}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
            مرحلهٔ ثبت سریال
          </label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {SOURCE_MODE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={sourceMode === opt.value ? "btn btn-primary" : "btn btn-secondary"}
                onClick={() => setSourceMode(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
            ایستگاه فعلی
          </label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <select
              value={routingOperationId ?? ""}
              onChange={(e) => setStation({ routingOperationId: e.target.value || null })}
              dir="ltr"
              style={{ flex: 1, padding: "0.6rem 0.9rem", borderRadius: "8px", background: "#fff", border: "1px solid #ccc" }}
            >
              <option value="">انتخاب RoutingOperation...</option>
              {routingOperationsData?.results?.map((op: any) => (
                <option key={op.id} value={op.id}>
                  {op.operation_code} — {op.operation_name}
                </option>
              ))}
            </select>
            <select
              value={workOrderId ?? ""}
              onChange={(e) => setStation({ workOrderId: e.target.value || null })}
              dir="ltr"
              style={{ flex: 1, padding: "0.6rem 0.9rem", borderRadius: "8px", background: "#fff", border: "1px solid #ccc" }}
            >
              <option value="">انتخاب WorkOrder (اختیاری)...</option>
              {workOrdersData?.results?.map((wo: any) => (
                <option key={wo.id} value={wo.id}>
                  WO #{wo.sequence_no} — {wo.status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <BarcodeScanner
          label="شمارهٔ سریال (تایپ یا اسکن)"
          placeholder="SN-00001"
          onSubmit={handleSubmit}
          disabled={isPending}
        />

        {isPending && <p style={{ marginTop: "1rem" }}>در حال ثبت…</p>}

        {errorMessage && (
          <div
            role="alert"
            style={{
              marginTop: "1rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              background: "#fef2f2",
              color: "#b91c1c",
              fontSize: "0.85rem",
            }}
          >
            {errorMessage}
          </div>
        )}

        {lastCreated && !errorMessage && (
          <div
            style={{
              marginTop: "1rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              background: "#f0fdf4",
              color: "#166534",
              fontSize: "0.85rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              سریال <strong dir="ltr">{lastCreated}</strong> ثبت شد.
            </span>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/production/serialization/trace?serial_number=${lastCreated}`)}
            >
              مشاهدهٔ ردیابی
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
